export const DEFAULT_BIBLE_VERSION = 'NKJV';

const VERSION_SUFFIX_PATTERN = /\s*\(([A-Z0-9][A-Z0-9 .-]{1,20})\)\s*$/i;

export function unescapeVerseValue(value) {
  return String(value || '')
    .replace(/\\(.)/g, '$1')
    .trim();
}

function normalizeVersion(value) {
  const cleaned = unescapeVerseValue(value)
    .replace(/^\((.*)\)$/, '$1')
    .trim();
  return cleaned ? cleaned.toUpperCase() : '';
}

function getReferenceParts(reference) {
  const cleaned = unescapeVerseValue(reference);
  const versionMatch = cleaned.match(VERSION_SUFFIX_PATTERN);

  if (!versionMatch) {
    return {
      reference: cleaned,
      version: '',
    };
  }

  return {
    reference: cleaned.replace(VERSION_SUFFIX_PATTERN, '').trim(),
    version: normalizeVersion(versionMatch[1]),
  };
}

export function getVerseReference(reference) {
  return getReferenceParts(reference).reference;
}

export function getBibleVersion(reference, version) {
  return normalizeVersion(version) || getReferenceParts(reference).version || DEFAULT_BIBLE_VERSION;
}

export function formatVerseReference(reference, version) {
  const cleanReference = getVerseReference(reference);
  const bibleVersion = getBibleVersion(reference, version);

  return cleanReference ? `${cleanReference} (${bibleVersion})` : bibleVersion;
}

// YouVersion deep links. The app opens bible.com; these maps translate a
// reference into the USFM book code and numeric version id that URL needs.
const YOUVERSION_VERSIONS = {
  NKJV: 114,
  NIV: 111,
  NLT: 116,
  ESV: 59,
  KJV: 1,
  NASB: 100,
  MSG: 97,
  AMP: 1588,
};

const YOUVERSION_BOOKS = {
  genesis: 'GEN',
  exodus: 'EXO',
  leviticus: 'LEV',
  numbers: 'NUM',
  deuteronomy: 'DEU',
  joshua: 'JOS',
  judges: 'JDG',
  ruth: 'RUT',
  '1samuel': '1SA',
  '2samuel': '2SA',
  '1kings': '1KI',
  '2kings': '2KI',
  '1chronicles': '1CH',
  '2chronicles': '2CH',
  ezra: 'EZR',
  nehemiah: 'NEH',
  esther: 'EST',
  job: 'JOB',
  psalm: 'PSA',
  psalms: 'PSA',
  proverbs: 'PRO',
  ecclesiastes: 'ECC',
  songofsolomon: 'SNG',
  isaiah: 'ISA',
  jeremiah: 'JER',
  lamentations: 'LAM',
  ezekiel: 'EZK',
  daniel: 'DAN',
  hosea: 'HOS',
  joel: 'JOL',
  amos: 'AMO',
  obadiah: 'OBA',
  jonah: 'JON',
  micah: 'MIC',
  nahum: 'NAM',
  habakkuk: 'HAB',
  zephaniah: 'ZEP',
  haggai: 'HAG',
  zechariah: 'ZEC',
  malachi: 'MAL',
  matthew: 'MAT',
  mark: 'MRK',
  luke: 'LUK',
  john: 'JHN',
  acts: 'ACT',
  romans: 'ROM',
  '1corinthians': '1CO',
  '2corinthians': '2CO',
  galatians: 'GAL',
  ephesians: 'EPH',
  philippians: 'PHP',
  colossians: 'COL',
  '1thessalonians': '1TH',
  '2thessalonians': '2TH',
  '1timothy': '1TI',
  '2timothy': '2TI',
  titus: 'TIT',
  philemon: 'PHM',
  hebrews: 'HEB',
  james: 'JAS',
  '1peter': '1PE',
  '2peter': '2PE',
  '1john': '1JN',
  '2john': '2JN',
  '3john': '3JN',
  jude: 'JUD',
  revelation: 'REV',
};

export function buildYouVersionUrl(rawReference, rawVersion) {
  const version = getBibleVersion(rawReference, rawVersion);
  const versionId = YOUVERSION_VERSIONS[version] ?? YOUVERSION_VERSIONS.NKJV;
  const refOnly = getVerseReference(rawReference);
  const m = refOnly.match(/^(\d?\s?[A-Za-z]+\.?)\s+(\d+):(\d+(?:-\d+)?)$/);
  if (m) {
    const bookKey = m[1].toLowerCase().replace(/\s|\./g, '');
    const usfm = YOUVERSION_BOOKS[bookKey];
    const chapter = m[2];
    const verses = m[3];
    if (usfm) {
      return `https://www.bible.com/bible/${versionId}/${usfm}.${chapter}.${verses}.${version}`;
    }
  }
  return `https://www.bible.com/search/bible?q=${encodeURIComponent(refOnly)}&version_id=${versionId}`;
}
